import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { api } from "../lib/api";
import { PencilIcon } from "./ui/icons";
import { cn } from "./ui/cn";

/**
 * The ticket's name, and the way to change it.
 *
 * A subject arrives from the customer and is usually right, but "Re: Fwd: FW:
 * your order", "Hi" and a whole quoted sentence are all real, and whichever it
 * is becomes what the queue, search, the SLA report and every notification
 * call this conversation. The fix has to be here, on the thing itself, rather
 * than in a settings screen an agent will not open mid-triage.
 *
 * ## Why in place rather than in a dialog
 *
 * The old name is the only context the new one needs, and it is already on
 * screen. A dialog would reprint it, take a click to open and a click to
 * dismiss, and put a modal between an agent and the conversation they are
 * halfway through reading -- for a change that is one line of text.
 *
 * ## Why the pencil is inside the button
 *
 * A heading that silently turns into a field when clicked is a thing you find
 * by accident or never, so there has to be a mark. It is part of the same
 * control rather than a second one beside it: two buttons in the accessibility
 * tree for one action reads as a choice the user does not have, and a pencil
 * with its own hit area is a four-pixel target next to a very large one.
 *
 * ## Why the heading wraps, and the field is a textarea
 *
 * A customer writing in about four order numbers puts all four in the subject,
 * and this used to be a single truncated line -- the first few words, then an
 * ellipsis, with no way to read the rest short of zooming the browser out. Two
 * problems in one: at rest it was unreadable, and the one gesture that would
 * naturally reveal it (clicking) was already spoken for by renaming.
 *
 * So both sides give. The heading clamps at two lines rather than one, which
 * is the whole subject for very nearly all of them, and the editor is a
 * textarea that grows to fit rather than an input that scrolls sideways -- so
 * the click that was always going to happen shows the rest as a side effect of
 * what it already did. Newlines are still refused on the way in; this is a
 * header field and Enter means commit, not paragraph.
 *
 * ## Why hovering shows the rest
 *
 * Two lines was not enough for the desk, and clicking to read is clicking to
 * rename -- a field full of selected text is a strange way to read a sentence,
 * and one stray keystroke from losing it. The native `title` tooltip was
 * already there and nobody found it: it waits a second, it is small, and it
 * looks like the browser's rather than ours.
 *
 * So a clipped subject shows in full in a panel under the heading the moment
 * the pointer is on it, or the control has keyboard focus. Only when it is
 * actually clipped -- measured on the way in, because a subject that fits
 * would just be said twice -- and over the thread rather than pushing it down,
 * so reading the subject does not move the thing you were about to read next.
 */
export function TicketSubject({
  ticketId,
  subject,
  onSaved,
}: {
  ticketId: number;
  subject: string;
  /** Refetch the ticket and the lists it appears in. */
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(subject);
  const field = useRef<HTMLTextAreaElement>(null);
  const heading = useRef<HTMLSpanElement>(null);
  /* Whether the heading is cutting the subject off, as of the last hover. */
  const [clipped, setClipped] = useState(false);
  const measure = () => {
    const element = heading.current;
    if (element) setClipped(element.scrollHeight > element.clientHeight + 1);
  };

  const save = useMutation({
    mutationFn: (next: string) => api.setSubject(ticketId, next),
    onSuccess: () => {
      setEditing(false);
      onSaved();
    },
  });

  /*
   * Opened with the whole name selected, because the common edit is "this name
   * is wrong" rather than "this name needs a word adding" -- typing should
   * replace it. Someone who wants to amend presses End or clicks.
   */
  useEffect(() => {
    if (editing) field.current?.select();
  }, [editing]);

  /*
   * Grow to the content on every change, not just on open.
   *
   * Reset to `auto` first: scrollHeight never shrinks below the height already
   * set, so measuring without clearing it leaves a field that only ever gets
   * taller as somebody deletes what they typed.
   */
  useEffect(() => {
    const node = field.current;
    if (!node) return;
    node.style.height = "auto";
    node.style.height = `${node.scrollHeight}px`;
  }, [draft, editing]);

  const open = () => {
    setDraft(subject);
    save.reset();
    setEditing(true);
  };

  const commit = () => {
    const next = draft.trim();
    /*
     * An empty or unchanged name is not a rename. Both close the field rather
     * than reporting an error: the agent has already decided not to make a
     * change, and telling them off for it is noise.
     */
    if (!next || next === subject) {
      setEditing(false);
      return;
    }
    save.mutate(next);
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={open}
        onMouseEnter={measure}
        onFocus={measure}
        aria-label={`${subject} (click to rename)`}
        className="group/subject relative flex min-w-0 items-start gap-1.5 rounded text-left hover:text-brand-600 dark:hover:text-brand-400"
      >
        <span ref={heading} className="min-w-0 line-clamp-2 text-base font-semibold">
          {subject}
        </span>
        {clipped ? (
          <span
            role="tooltip"
            className={cn(
              "pointer-events-none absolute left-0 top-full z-30 mt-1 hidden w-max max-w-[min(42rem,80vw)]",
              "whitespace-normal break-words rounded-md border border-line bg-surface px-3 py-2",
              "text-sm font-medium text-content shadow-lg",
              "group-hover/subject:block group-focus-visible/subject:block",
            )}
          >
            {subject}
          </span>
        ) : null}
        {/*
          Visible on hover and whenever the control has keyboard focus -- a mark
          that only answers to a mouse is no mark at all for somebody tabbing
          through the header.

          Nudged down to sit on the first line rather than centred on the block,
          because a two-line subject would otherwise float it into the gap
          between the lines, pointing at nothing.
        */}
        <PencilIcon
          className={cn(
            "mt-1 h-3.5 w-3.5 shrink-0 text-faint opacity-0 transition-opacity",
            "group-hover/subject:opacity-100 group-focus-visible/subject:opacity-100",
          )}
        />
      </button>
    );
  }

  return (
    <div className="min-w-0">
      <textarea
        ref={field}
        rows={1}
        value={draft}
        onChange={(event) =>
          /*
           * Strip newlines here rather than only blocking the Enter key: a
           * pasted subject carries the line breaks of wherever it was copied
           * from, and a Subject header cannot hold one.
           */
          setDraft(event.target.value.replace(/[\r\n]+/g, " "))
        }
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            commit();
          }
          if (event.key === "Escape") {
            event.preventDefault();
            /*
             * The draft goes back before the field closes, not after. Closing
             * unmounts a focused field, and whether that fires onBlur is a
             * browser detail -- restoring first means commit() sees an
             * unchanged name and does nothing either way.
             */
            setDraft(subject);
            setEditing(false);
          }
        }}
        disabled={save.isPending}
        aria-label="Ticket name"
        maxLength={998}
        className={cn(
          "w-full resize-none overflow-hidden rounded border border-brand-500",
          "bg-surface px-1.5 py-0.5 text-base font-semibold text-content outline-none",
          save.isPending && "opacity-60",
        )}
      />
      {save.isError ? (
        <p className="mt-0.5 text-[11px] text-sla-breached">
          That name could not be saved.
        </p>
      ) : null}
    </div>
  );
}
